
import React, { useEffect, useRef } from "react";
import { isLowPowerMode } from "../../services/memoryService";

interface WaveformMiniProps {
  analyser: AnalyserNode | null;
  isActive: boolean;
  bpm?: number;
  timeSignature?: string;
  transparent?: boolean;
}

/**
 * WaveformMini v2.9 - Neural Signal Probe
 */
export const WaveformMini: React.FC<WaveformMiniProps> = ({ 
  analyser, 
  isActive, 
  bpm = 90, 
  timeSignature = "4/4",
  transparent = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const historyRef = useRef<number[]>([]);
  const skipCounter = useRef(0);
  
  const beatsPerBar = parseInt(timeSignature.split('/')[0]) || 4;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser) return;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const dpr = isIOS ? 1 : (window.devicePixelRatio || 1);
    
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    const ctx = canvas.getContext("2d", { alpha: true }); 
    if (!ctx) return;
    
    ctx.scale(dpr, dpr);

    const buffer = new Float32Array(analyser.fftSize);
    const historySize = isIOS ? 400 : 800; 
    
    if (historyRef.current.length === 0) {
        historyRef.current = new Array(historySize).fill(0);
    }

    const draw = () => {
      const throttle = isLowPowerMode() ? 2 : 0;
      if (skipCounter.current < throttle) {
          skipCounter.current++;
          rafRef.current = requestAnimationFrame(draw);
          return;
      }
      skipCounter.current = 0;

      if (!isActive) {
          rafRef.current = requestAnimationFrame(draw);
          return;
      }
      
      analyser.getFloatTimeDomainData(buffer);
      const { width, height } = rect; 
      
      let maxAmp = 0;
      const step = Math.floor(buffer.length / 32); 
      for (let i = 0; i < buffer.length; i += step) {
          const a = Math.abs(buffer[i]);
          if (a > maxAmp) maxAmp = a;
      }

      historyRef.current.push(maxAmp);
      if (historyRef.current.length > historySize) {
          historyRef.current.shift();
      }

      ctx.clearRect(0, 0, width, height);
      
      const mid = height / 2;
      const msPerPixel = 16.67; 
      const secPerBeat = 60 / bpm;
      const pixelsPerBeat = (secPerBeat * 1000) / msPerPixel;

      ctx.lineWidth = 1;
      
      ctx.beginPath();
      for (let i = 0; i < width; i+=2) { 
          const revIdx = historyRef.current.length - 1 - i;
          if (revIdx < 0) break;
          
          const x = width - i;
          if (Math.floor(revIdx % pixelsPerBeat) === 0) {
              const beatNum = Math.floor(revIdx / pixelsPerBeat);
              const isBar = beatNum % beatsPerBar === 0;
              
              ctx.stroke(); 
              ctx.beginPath();
              ctx.strokeStyle = isBar ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.05)";
              ctx.moveTo(x, 0);
              ctx.lineTo(x, height);
              ctx.stroke();
              ctx.beginPath(); 
          }
      }

      ctx.beginPath();
      ctx.strokeStyle = "#FF6261";
      ctx.lineWidth = 2; 
      ctx.lineJoin = "round";
      ctx.shadowBlur = 4;
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      
      const visualGain = 5.0;

      for (let i = 0; i < historyRef.current.length; i++) {
        const val = historyRef.current[i] * visualGain;
        const x = (i / historySize) * width;
        const h = Math.min(mid, val * mid);
        
        ctx.moveTo(x, mid - h);
        ctx.lineTo(x, mid + h);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    
    return () => { 
        if (rafRef.current) cancelAnimationFrame(rafRef.current); 
    };
  }, [analyser, isActive, bpm, beatsPerBar]);

  if (transparent) {
      return (
        <div className="w-full h-24 relative">
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} className="w-full h-full opacity-100" />
        </div>
      );
  }

  return (
    <div className="w-full bg-black/40 rounded-full border border-white/10 px-6 py-2 backdrop-blur-3xl overflow-hidden flex items-center gap-4">
      <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest italic shrink-0">Signal</span>
      <canvas ref={canvasRef} style={{ width: '120px', height: '24px' }} className="shrink-0" />
      <span className="text-[8px] font-mono text-cyan-400 shrink-0">{bpm} BPM</span>
    </div>
  );
};
